/* MultiKindSchriftjeDialog — automatische multi-kind flow voor het AI-schriftje.
 *
 * Wordt getriggerd wanneer een spraakopname 2+ bekende kindnamen bevat.
 * Roept POST /api/schriftje/split-per-kind aan, toont per kind een aparte kaart
 * met controle op:
 *   - Welk kind gekoppeld is (dropdown met fuzzy-match, "Kind selecteren" bij twijfel)
 *   - Oorspronkelijk transcriptfragment (read-only)
 *   - AI-gegenereerd schriftje (bewerkbare Textarea, per kind apart AI-call)
 *   - Individuele verzend-checkbox (default UIT — nooit auto-verzenden)
 *
 * Foutafhandeling:
 *   - Lege transcript → 400 error, dialog niet openen
 *   - Split geeft 0 kinderen terug → toon één item met "Kind selecteren"
 *   - Onbekende naam → altijd "Kind selecteren" verplicht voor verzending
 *   - Dubbele match → visuele waarschuwing "Zelfde kind al elders geselecteerd"
 *   - Netwerkfout op AI-generatie → toont retry-knop, blokkeert alleen dat kind
 *   - Geen enkel item geselecteerd → verzend-knop disabled
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Sparkles, AlertTriangle, RefreshCw, Loader2, CheckCircle2, User } from "lucide-react";
import { toast } from "sonner";
import { localToday } from "@/lib/format";

// Fuzzy match: normaliseer + Levenshtein-achtige similarity op voornamen.
function normalizeName(s) {
  return (s || "").toString().trim().toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]/g, "");
}

function similarity(a, b) {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  // Substring/prefix boost — Whisper hakt vaak einden af of voegt letters toe.
  if (na.startsWith(nb) || nb.startsWith(na)) return 0.85;
  if (na.includes(nb) || nb.includes(na)) return 0.75;
  // Levenshtein via DP (klein: max 20 tekens)
  const m = Math.min(na.length, 20);
  const n = Math.min(nb.length, 20);
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++)
    for (let j = 1; j <= n; j++)
      dp[i][j] = na[i-1] === nb[j-1]
        ? dp[i-1][j-1]
        : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
  return 1 - dp[m][n] / Math.max(m, n);
}

function bestKindMatch(name, kinderen) {
  if (!name || !kinderen?.length) return null;
  let best = null;
  let bestScore = 0;
  for (const k of kinderen) {
    const sc = Math.max(
      similarity(name, k.voornaam),
      similarity(name, `${k.voornaam} ${k.achternaam || ""}`),
    );
    if (sc > bestScore) { bestScore = sc; best = k; }
  }
  // Threshold 0.7: onder deze grens = onzeker, gebruiker moet handmatig kiezen
  return bestScore >= 0.7 ? { kind: best, score: bestScore } : null;
}

// Detecteer namen uit vrij transcript — retourneert count van unieke matches
export function countKnownKinderenInTranscript(transcript, kinderen) {
  if (!transcript || !kinderen?.length) return 0;
  const t = normalizeName(transcript);
  const found = new Set();
  for (const k of kinderen) {
    const nv = normalizeName(k.voornaam);
    if (nv && nv.length >= 3 && t.includes(nv)) found.add(k.id);
  }
  return found.size;
}

export default function MultiKindSchriftjeDialog({
  open, onClose, transcript, kinderen, defaultDatum,
}) {
  const [splitting, setSplitting] = useState(false);
  const [items, setItems] = useState([]); // {id, naam, fragment, selected_kind_id, ai_text, ai_error, ai_loading, send}
  const [sendingAll, setSendingAll] = useState(false);
  const [saveErrors, setSaveErrors] = useState({}); // itemId -> error msg
  const [lengte, setLengte] = useState("kort"); // kort | gemiddeld | uitgebreid

  const bekendeNamen = useMemo(
    () => (kinderen || []).map(k => k.voornaam).filter(Boolean),
    [kinderen]
  );

  // Split de transcript per kind zodra dialog opent
  useEffect(() => {
    if (!open || !transcript?.trim()) return;
    setSplitting(true);
    setSaveErrors({});
    (async () => {
      try {
        const r = await api.post("/schriftje/split-per-kind", {
          transcript,
          bekende_namen: bekendeNamen,
        });
        const raw = r.data?.kinderen || [];
        // Enrich met kind-match
        const enriched = raw.map((it, idx) => {
          const match = bestKindMatch(it.naam, kinderen);
          return {
            id: `split-${idx}`,
            naam: it.naam || "",
            fragment: it.fragment || "",
            selected_kind_id: match?.kind?.id || "",
            match_score: match?.score || 0,
            ai_text: "",
            ai_error: null,
            ai_loading: false,
            send: false, // NOOIT default true — expliciete bevestiging vereist
          };
        });
        setItems(enriched);
        if (enriched.length === 0) {
          toast.error("Geen fragmenten herkend — probeer opnieuw op te nemen");
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Splitsen mislukt");
        onClose?.();
      } finally { setSplitting(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transcript, bekendeNamen.join("|")]);

  const generateForItem = useCallback(async (itemId) => {
    setItems(prev => prev.map(i =>
      i.id === itemId ? { ...i, ai_loading: true, ai_error: null } : i));
    const item = items.find(i => i.id === itemId);
    const kind = kinderen.find(k => k.id === item?.selected_kind_id);
    if (!item || !kind) {
      setItems(prev => prev.map(i =>
        i.id === itemId ? { ...i, ai_loading: false, ai_error: "Selecteer eerst een kind" } : i));
      return;
    }
    try {
      const r = await api.post("/schriftje/ai-assist", {
        kind_naam: `${kind.voornaam} ${kind.achternaam || ""}`.trim(),
        datum: defaultDatum || localToday(),
        ruwe_notities: item.fragment,
        gewenste_taal: "Nederlands",
        gewenste_lengte: lengte,
      });
      const tekst = r.data?.nederlands || "";
      setItems(prev => prev.map(i =>
        i.id === itemId ? { ...i, ai_text: tekst, ai_loading: false } : i));
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || "AI-generatie mislukt";
      setItems(prev => prev.map(i =>
        i.id === itemId ? { ...i, ai_loading: false, ai_error: msg } : i));
    }
  }, [items, kinderen, defaultDatum]);

  // Bij wissel van lengte-preset: wis alle huidige AI-teksten zodat ze automatisch
  // opnieuw gegenereerd worden met de nieuwe lengte-instructie.
  useEffect(() => {
    if (splitting || !items.length) return;
    setItems(prev => prev.map(i =>
      i.ai_text ? { ...i, ai_text: "", ai_error: null } : i
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lengte]);

  // Auto-genereer AI voor items met een matching kind (score >= 0.7)
  useEffect(() => {
    if (splitting || !items.length) return;
    items.forEach(i => {
      if (i.selected_kind_id && !i.ai_text && !i.ai_loading && !i.ai_error) {
        generateForItem(i.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splitting, items.length]);

  const duplicateKindIds = useMemo(() => {
    const counts = {};
    items.forEach(i => {
      if (i.selected_kind_id) counts[i.selected_kind_id] = (counts[i.selected_kind_id] || 0) + 1;
    });
    return new Set(Object.keys(counts).filter(k => counts[k] > 1));
  }, [items]);

  const toSend = items.filter(i => i.send && i.selected_kind_id && i.ai_text.trim());
  const canSend = toSend.length > 0 && !sendingAll;

  const sendSelected = async () => {
    setSendingAll(true);
    const errors = {};
    let successCount = 0;
    for (const it of toSend) {
      try {
        await api.post("/schriftje", {
          kind_id: it.selected_kind_id,
          datum: defaultDatum || localToday(),
          activiteiten: it.ai_text,
          verzonden: true,
        });
        successCount += 1;
      } catch (e) {
        errors[it.id] = formatApiError(e.response?.data?.detail) || "Opslaan mislukt";
      }
    }
    setSaveErrors(errors);
    setSendingAll(false);
    if (successCount > 0) {
      toast.success(`${successCount} schriftje${successCount === 1 ? "" : "s"} verzonden`);
    }
    if (Object.keys(errors).length === 0 && successCount > 0) {
      onClose?.();
    } else if (Object.keys(errors).length > 0) {
      toast.error(`${Object.keys(errors).length} schriftje${Object.keys(errors).length === 1 ? "" : "s"} niet opgeslagen`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto"
                     data-testid="multi-kind-schriftje-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[color:var(--terracotta)]" />
            Multi-kind schriftje — controle
          </DialogTitle>
          <DialogDescription>
            Er zijn meerdere kinderen herkend in je opname. Controleer per kind of
            de koppeling klopt en het gegenereerde schriftje juist is. Vink
            handmatig aan welke schriftjes je wilt verzenden.
          </DialogDescription>
        </DialogHeader>

        {splitting && (
          <div className="flex items-center gap-3 justify-center py-8 text-sm text-neutral-600"
               data-testid="multi-kind-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Fragmenten splitsen…
          </div>
        )}

        {!splitting && items.length > 0 && (
          <div className="flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 mt-2"
               data-testid="multi-kind-lengte-bar">
            <span className="text-[11px] font-medium text-neutral-700">Lengte per verhaal:</span>
            {["kort", "gemiddeld", "uitgebreid"].map(l => (
              <Button key={l} size="sm" variant={lengte === l ? "default" : "outline"}
                      onClick={() => setLengte(l)}
                      className="h-6 px-2 text-[10px] capitalize"
                      style={lengte === l ? { background: "var(--sage)" } : {}}
                      data-testid={`multi-kind-lengte-${l}`}>
                {l}
              </Button>
            ))}
            <span className="text-[10px] text-neutral-500 flex-1">
              Kort ≈ 60-80w · Gemiddeld ≈ 120-160w · Uitgebreid ≈ 220-280w
            </span>
          </div>
        )}

        {!splitting && items.length === 0 && (
          <div className="text-center py-6 text-sm text-neutral-500">
            Geen kind-fragmenten herkend. Probeer opnieuw op te nemen en noem de
            namen expliciet.
          </div>
        )}

        {!splitting && items.length > 0 && (
          <div className="space-y-3 mt-2">
            {items.map((it, idx) => {
              const kind = kinderen.find(k => k.id === it.selected_kind_id);
              const isDuplicate = duplicateKindIds.has(it.selected_kind_id);
              const isUncertain = !it.selected_kind_id;
              return (
                <div key={it.id}
                     className={`rounded-lg border p-3 ${isDuplicate ? "border-amber-300 bg-amber-50/40" : isUncertain ? "border-red-300 bg-red-50/30" : "border-neutral-200 bg-white"}`}
                     data-testid={`multi-kind-card-${idx}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">
                        Fragment #{idx + 1} · {it.naam ? `AI hoorde: "${it.naam}"` : "Geen naam gedetecteerd"}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select value={it.selected_kind_id || ""}
                                onValueChange={(v) => setItems(prev => prev.map(i =>
                                  i.id === it.id
                                    ? { ...i, selected_kind_id: v, ai_text: "", ai_error: null }
                                    : i))}>
                          <SelectTrigger className="w-64 h-9"
                                         data-testid={`multi-kind-select-${idx}`}>
                            <SelectValue placeholder="Kind selecteren…" />
                          </SelectTrigger>
                          <SelectContent className="bg-white max-h-72">
                            {kinderen.map(k => (
                              <SelectItem key={k.id} value={k.id}>
                                <span className="flex items-center gap-1.5">
                                  <User className="h-3 w-3" />
                                  {k.voornaam} {k.achternaam || ""}
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {kind && it.match_score >= 0.85 && (
                          <Badge variant="outline"
                                 className="border-emerald-300 text-emerald-700 text-[10px]">
                            <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Match
                          </Badge>
                        )}
                        {kind && it.match_score >= 0.7 && it.match_score < 0.85 && (
                          <Badge variant="outline"
                                 className="border-amber-300 text-amber-700 text-[10px]">
                            <AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> Onzeker
                          </Badge>
                        )}
                        {isDuplicate && (
                          <Badge variant="outline"
                                 className="border-amber-500 text-amber-800 text-[10px]"
                                 data-testid={`multi-kind-duplicate-${idx}`}>
                            <AlertTriangle className="h-2.5 w-2.5 mr-0.5" /> Al elders gekozen
                          </Badge>
                        )}
                      </div>
                    </div>
                    <label className="flex items-center gap-1.5 shrink-0 cursor-pointer">
                      <Checkbox
                        checked={it.send}
                        disabled={!it.selected_kind_id || !it.ai_text.trim() || sendingAll}
                        onCheckedChange={(v) => setItems(prev => prev.map(i =>
                          i.id === it.id ? { ...i, send: !!v } : i))}
                        data-testid={`multi-kind-send-${idx}`}
                      />
                      <span className="text-xs">Verzenden</span>
                    </label>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                    <div>
                      <Label className="text-[11px] text-neutral-500">
                        Opgenomen fragment (read-only)
                      </Label>
                      <div className="mt-1 p-2 text-xs bg-neutral-50 border border-neutral-200 rounded-md whitespace-pre-wrap"
                           data-testid={`multi-kind-fragment-${idx}`}>
                        {it.fragment || <em className="text-neutral-400">Geen fragment</em>}
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-[11px] text-neutral-500">
                          Gegenereerd schriftje (bewerkbaar)
                        </Label>
                        <Button
                          size="sm" variant="ghost"
                          className="h-6 px-1.5 text-[10px]"
                          disabled={!it.selected_kind_id || it.ai_loading}
                          onClick={() => generateForItem(it.id)}
                          data-testid={`multi-kind-regen-${idx}`}
                        >
                          <RefreshCw className="h-2.5 w-2.5 mr-0.5" />
                          {it.ai_text ? "Opnieuw" : "Genereren"}
                        </Button>
                      </div>
                      {it.ai_loading && (
                        <div className="mt-1 flex items-center gap-2 text-xs text-neutral-500">
                          <Loader2 className="h-3 w-3 animate-spin" /> AI schrijft…
                        </div>
                      )}
                      {it.ai_error && (
                        <div className="mt-1 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-1.5"
                             data-testid={`multi-kind-error-${idx}`}>
                          {it.ai_error}
                        </div>
                      )}
                      <Textarea
                        rows={5} value={it.ai_text}
                        onChange={(e) => setItems(prev => prev.map(i =>
                          i.id === it.id ? { ...i, ai_text: e.target.value } : i))}
                        placeholder={it.selected_kind_id
                          ? "Klik 'Genereren' voor een AI-versie of typ zelf"
                          : "Selecteer eerst een kind"}
                        disabled={!it.selected_kind_id}
                        className="mt-1 text-xs"
                        data-testid={`multi-kind-text-${idx}`}
                      />
                    </div>
                  </div>
                  {saveErrors[it.id] && (
                    <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-1.5"
                         data-testid={`multi-kind-save-error-${idx}`}>
                      Verzenden mislukt: {saveErrors[it.id]}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 mt-3">
          <Button variant="ghost" onClick={onClose} disabled={sendingAll}
                  data-testid="multi-kind-cancel">
            Annuleren
          </Button>
          <div className="flex-1 text-[11px] text-neutral-500 self-center">
            {toSend.length > 0
              ? `${toSend.length} van ${items.length} geselecteerd voor verzending`
              : "Vink kinderen aan om te verzenden — nooit automatisch"}
          </div>
          <Button
            onClick={sendSelected}
            disabled={!canSend}
            data-testid="multi-kind-send-btn"
            style={{ background: "var(--sage)" }}
            className="text-white"
          >
            {sendingAll
              ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Verzenden…</>
              : `Verzend ${toSend.length} schriftje${toSend.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
